import type { QueryOptionsOverride } from '../queryOptions.helpers';
import type { GameDto, GameListDto, GamesFiltersParamsPayload } from './games.schema';
import {
  keepPreviousData,
  queryOptions,
} from '@tanstack/react-query';
import { QueriesKeys } from '../queriesKeys';
import { PUBLIC_QUERY_STALE_TIME } from '../queryOptions.helpers';
import { gamesApi } from './games.api';

export const GAMES_BY_CATEGORY_PAGE_SIZE = 32;
export const FAVORITES_MAX_ITEMS = 100;

type GameQueryOptions = { options?: QueryOptionsOverride<GameListDto, Error> };

type GamesListQueryArgs = {
  params?: GamesFiltersParamsPayload;
  scope?: 'default' | 'searchRecent' | 'missionGames';
} & GameQueryOptions;

type GameByCategoryIdQueryArgs = {
  page?: number;
  categoryId: string;
  searchValue?: string;
} & GameQueryOptions;

const buildGamesByCategoryParams = (
  categoryId: string,
  page = 1,
  searchValue?: string,
) => {
  const normalizedSearch = searchValue?.trim().toLowerCase() || null;

  return {
    normalizedSearch,
    params: {
      page,
      limit: GAMES_BY_CATEGORY_PAGE_SIZE,
      filters: {
        categoryId,
        ...(normalizedSearch ? { name: normalizedSearch } : {}),
      },
    },
  };
};

const withFavoriteCreatedAtSort = (params?: GamesFiltersParamsPayload) => ({
  ...params,
  sort: {
    ...params?.sort,
    favouriteCreatedAt: 'asc',
  },
});

export const gameQueryOptions = {
  /**
   * Fetch a single game by its ID or alias.
   */
  one: (id: string, options?: QueryOptionsOverride<GameDto>) => queryOptions({
    queryKey: [QueriesKeys.gameById, id],
    queryFn: () => gamesApi.fetchById(id),
    staleTime: PUBLIC_QUERY_STALE_TIME,
    ...options,
  }),

  /**
   * Fetch list of games with optional filters.
   */
  list: (args: GamesListQueryArgs = {}) => {
    const { options, scope = 'default', params } = args;

    return queryOptions({
      queryFn: () => gamesApi.fetchAll(params),
      queryKey: [QueriesKeys.games, 'list', scope, params],
      ...options,
    });
  },

  /**
   * Fetch list of games by category id with search and pagination.
   */
  listByCategory: (args: GameByCategoryIdQueryArgs) => {
    const { categoryId, searchValue, page = 1, options } = args;
    const { params, normalizedSearch } = buildGamesByCategoryParams(categoryId, page, searchValue);

    return queryOptions({
      queryKey: [QueriesKeys.games, 'byCategory', categoryId, page, normalizedSearch],
      queryFn: () => gamesApi.fetchAll(params),
      ...options,
    });
  },

  /**
   * Fetch similar games by game ID.
   */
  similarById: (id: string, options?: QueryOptionsOverride<GameDto[]>) => queryOptions({
    queryKey: [QueriesKeys.gamesSimilar, id],
    queryFn: () => gamesApi.getSimilarGamesById(id),
    staleTime: PUBLIC_QUERY_STALE_TIME,
    ...options,
  }),

  /**
   * Fetch list of user's favorite games with optional filters.
   */
  favoritesList: ({ params, options }: GamesListQueryArgs = {}) => {
    const paramsWithDefaultSort = withFavoriteCreatedAtSort(params);

    return queryOptions({
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      queryKey: [QueriesKeys.games, 'favorites', paramsWithDefaultSort],
      queryFn: () => gamesApi.getListOfFavoritesGames(paramsWithDefaultSort),
      ...options,
    });
  },
};

export const DEFAULT_GAME_QUERY_OPTIONS = {
  ssr: {
    // Public storefront data is hydrated from SSR and should not refetch
    // on client mount/focus just because the user session state changed.
    staleTime: PUBLIC_QUERY_STALE_TIME,
  },
  client: {
    // Game/slot detail pages are SSR-hydrated public data. Keep the hydrated
    // result fresh on the client so auth/session changes don't trigger a
    // duplicate public game request after mount.
    staleTime: PUBLIC_QUERY_STALE_TIME,
    refetchOnMount: false,
  },
} as const;
